import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { ZapIcon } from '../components/icons';

interface LocState { from?: string }

export function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true); setErr(null);
    try {
      await api.login(email, password);
      // Drop any cached 401 errors / stale data from a previous session.
      qc.clear();
      const from = (loc.state as LocState | null)?.from ?? '/';
      nav(from, { replace: true });
    } catch {
      setErr('Invalid email or password');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-700
                          text-white grid place-items-center shadow-lg shadow-accent-500/30">
            <ZapIcon size={22} />
          </div>
          <h1 className="font-bold text-2xl tracking-tight mt-4 text-slate-900">Welcome back</h1>
          <p className="text-sm text-slate-500 mt-1">Sign in to your fmon dashboard</p>
        </div>

        <form onSubmit={onSubmit}
              className="bg-white border border-slate-200 rounded-xl p-7 space-y-5
                         shadow-[0_4px_24px_rgba(15,23,42,0.06)]">
          <Field label="Email">
            <input className={fieldCls} type="email" required autoFocus
                   value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="admin@fmon.local" />
          </Field>
          <Field label="Password">
            <input className={fieldCls} type="password" required
                   value={password} onChange={(e) => setPassword(e.target.value)}
                   placeholder="••••••••" />
          </Field>

          {err && (
            <p className="text-rose-700 text-sm bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
              {err}
            </p>
          )}

          <button disabled={loading}
                  className="w-full h-10 bg-slate-900 text-white rounded-lg font-medium
                             hover:bg-slate-800 active:bg-slate-950 transition shadow-sm
                             disabled:opacity-50">
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">
          fmon · frontend monitoring
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const fieldCls =
  'w-full h-10 border border-slate-300 rounded-lg px-3 text-sm shadow-sm ' +
  'focus:outline-none focus:ring-4 focus:ring-accent-500/15 focus:border-accent-500 transition';
